import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import Collapse from '@mui/material/Collapse';
import Menu from '@mui/material/Menu';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import FleetDataSet from 'models/fleet-management/FleetDataSet';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'store';
import {
  selectFleetDataSetsData,
  selectFleetFieldsConfiguration,
} from 'store/fleet-management/fleet-management-slice-selectors';
import FilterInput from '../widget/FilterInput';
import TooltipIconButton from '@common/components/Inputs/TooltipIconButton';
import { AddIcon, CopyIcon } from '@common/utils/icons/icons';
import { FleetDataFilter } from 'models/fleet-management/FleetDataFilter';
import { FilterType, MissionDateFilter } from 'models/enums/fleet-management';
import DateInput from '@common/components/Inputs/DateInput';
import { DateTime } from 'luxon';
import { dateToStringFormat } from '@common/utils/date';

const DatasetFilters: React.FC<{
  dataset: FleetDataSet;
  isEditing: boolean;
  onEdit: (ds: Partial<FleetDataSet>) => void;
}> = ({ dataset, isEditing, onEdit }) => {
  const { t } = useTranslation();

  const datasets = useAppSelector((state) => selectFleetDataSetsData(state));
  const fieldsConfig = useAppSelector((state) => selectFleetFieldsConfiguration(state));

  const [dateFilterAnchorEl, setDateFilterAnchorEl] = useState<HTMLButtonElement | null>(null);

  const filterChangeHandler = (
    newValue: FleetDataFilter,
    filterIndex: number,
    toDelete?: boolean,
  ) => {
    const widgetFilters = [...(dataset?.filters ?? [])];
    if (toDelete) {
      widgetFilters.splice(filterIndex, 1);
    } else if (widgetFilters.length < filterIndex) {
      widgetFilters.push(newValue);
    } else {
      widgetFilters[filterIndex] = newValue;
    }
    onEdit({ filters: widgetFilters });
  };

  const dateFilterChangeHandler = (newValue: MissionDateFilter) => {
    onEdit({ missionDateFilter: newValue, start: null, end: null });
  };

  const startDateChangeHandler = (newStartDate: DateTime | null) => {
    if (newStartDate) {
      onEdit({ start: newStartDate.startOf('day').toISO() });
    }
  };

  const endDateChangeHandler = (newEndDate: DateTime | null) => {
    if (newEndDate) {
      onEdit({ end: newEndDate.endOf('day').toISO() });
    }
  };

  const openDateFilterMenuClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    setDateFilterAnchorEl(event.currentTarget);
  };

  const closeDateFilterMenuHandler = () => {
    setDateFilterAnchorEl(null);
  };

  const addFilterClickHandler = () => {
    onEdit({
      filters: [
        ...dataset.filters,
        { field: '', id: uuidv4(), type: FilterType.EXACT, values: [] },
      ],
    });
  };

  return (
    <React.Fragment>
      <Stack
        sx={(theme) => ({
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2,
          p: 2,
          minHeight: 0,
          overflow: 'hidden',
        })}
        spacing={2}
        flex={1}
      >
        <Stack direction="row" alignItems="center">
          <Typography variant="h2" flex={1} sx={(theme) => ({ lineHeight: theme.spacing(5) })}>
            {t('title-dataset-filters')}
          </Typography>
          {datasets != null && datasets.length > 1 && isEditing && (
            <TooltipIconButton icon={<CopyIcon />} tooltip={t('button-copy-from-dataset')} />
          )}
          {isEditing && (
            <Button size="small" startIcon={<AddIcon />} onClick={addFilterClickHandler}>
              {t('button-add')}
            </Button>
          )}
        </Stack>

        <Stack spacing={2} sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <Stack
            sx={(theme) => ({
              width: '100%',
              borderRadius: 1,
              p: 2,
              backgroundColor: theme.palette.action.selected,
            })}
            spacing={2}
          >
            <Stack direction="row" spacing={2}>
              <Button
                fullWidth
                disabled
                sx={(theme) => ({
                  backgroundColor: '#FFF',
                  boxShadow: 0,
                  '&.Mui-disabled': {
                    color: isEditing ? undefined : theme.palette.text.primary,
                    cursor: 'default',
                  },
                  height: theme.spacing(5),
                })}
              >
                {t('input-label-date-range')}
              </Button>
              <Button
                fullWidth
                onClick={openDateFilterMenuClickHandler}
                sx={(theme) => ({
                  backgroundColor: '#FFF',
                  boxShadow: 0,
                  height: theme.spacing(5),
                  '&.Mui-disabled': {
                    color: isEditing ? undefined : theme.palette.text.primary,
                    cursor: 'default',
                  },
                })}
                disabled={!isEditing}
              >
                {dataset.missionDateFilter}
              </Button>
            </Stack>
            <Collapse in={dataset.missionDateFilter === MissionDateFilter.CUSTOM}>
              <Stack direction="row" spacing={1} alignItems="center">
                {isEditing ? (
                  <DateInput
                    sx={{
                      backgroundColor: '#FFF',
                      height: 40,
                      '& .MuiInput-input': { height: 40, padding: 0, paddingLeft: 1 },
                      '& .MuiIconButton-root': { margin: 0 },
                    }}
                    borderRadius={1}
                    date={dataset.start != null ? DateTime.fromISO(dataset.start) : null}
                    onDateTimeChange={startDateChangeHandler}
                    maxDate={dataset.end != null ? DateTime.fromISO(dataset.end) : undefined}
                  />
                ) : (
                  <Typography
                    sx={(theme) => ({
                      flex: 1,
                      backgroundColor: theme.palette.background.paper,
                      lineHeight: theme.spacing(5),
                      borderRadius: 1,
                      textAlign: 'center',
                    })}
                  >
                    {dataset.start == null ? '' : dateToStringFormat(dataset.start, true)}
                  </Typography>
                )}
                <Typography variant="subtitle1">{t('input-label-to')}</Typography>
                {isEditing ? (
                  <DateInput
                    sx={{
                      backgroundColor: '#FFF',
                      height: 40,
                      '& .MuiInput-input': { height: 40, padding: 0, paddingLeft: 1 },
                      '& .MuiIconButton-root': { margin: 0 },
                    }}
                    borderRadius={1}
                    date={dataset.end != null ? DateTime.fromISO(dataset.end) : null}
                    onDateTimeChange={endDateChangeHandler}
                    minDate={dataset.start != null ? DateTime.fromISO(dataset.start) : undefined}
                  />
                ) : (
                  <Typography
                    sx={(theme) => ({
                      flex: 1,
                      backgroundColor: theme.palette.background.paper,
                      lineHeight: theme.spacing(5),
                      borderRadius: 1,
                      textAlign: 'center',
                    })}
                  >
                    {dataset.end == null ? '' : dateToStringFormat(dataset.end, true)}
                  </Typography>
                )}
              </Stack>
            </Collapse>
          </Stack>

          {dataset != null &&
            Array.from({ length: Math.max(dataset.filters.length, isEditing ? 1 : 0) }).map(
              (_, filterIndex) => (
                <FilterInput
                  disabled={!isEditing}
                  key={filterIndex}
                  fields={
                    // A filter-only field is never among the dataset's fields, so it is always offered.
                    fieldsConfig?.filter(
                      (f) =>
                        f.model === dataset.model &&
                        (f.filterOnly || dataset.fields?.includes(f.fieldName)),
                    ) ?? []
                  }
                  filterIndex={filterIndex}
                  datasetId={dataset.id}
                  value={
                    dataset.filters != null && dataset.filters.length >= filterIndex + 1
                      ? dataset.filters[filterIndex]
                      : null
                  }
                  onFilterChange={filterChangeHandler}
                />
              ),
            )}
        </Stack>
      </Stack>
      <Menu
        MenuListProps={{
          'aria-labelledby': 'fade-button',
        }}
        anchorEl={dateFilterAnchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        open={dateFilterAnchorEl != null}
        onClose={closeDateFilterMenuHandler}
      >
        <Box
          sx={(theme) => ({
            width: theme.spacing(32),
            maxHeight: theme.spacing(24),
          })}
        >
          {Object.values(MissionDateFilter).map((df) => (
            <MenuItem key={df} value={df} onClick={() => dateFilterChangeHandler(df)}>
              {df}
            </MenuItem>
          ))}
        </Box>
      </Menu>
    </React.Fragment>
  );
};

export default DatasetFilters;
